import { useCallback, useEffect, useState } from 'react'
import { ChevronRight, Home, Loader2, MapPin } from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { ErpTable, Td, Th } from '@/components/erp/primitives'
import { ErrorStrip, LoadingRows, Note } from '@/shared/ui/states'
import { fmtNumber } from '@/shared/lib/format'
import { UNIT_TYPE_LABEL, UNIT_TYPE_PLURAL } from '@/shared/api/types'
import { seatGeography, type GeographyBreakdown } from './api'

/**
 * Results by place, drilled down to the polling station.
 *
 * ## Why a drill-down and not one flat level
 *
 * The breakdown is always ONE level below where you are looking, because that is what the
 * grouped ltree query returns — a national view splits by county and nothing else. On its own
 * that is close to useless for a count: "Nairobi: 412 votes" is an aggregate of hundreds of
 * stations, and **the polling station is the smallest unit anything is actually counted at**.
 * Every figure above it is a sum of station rows, so the station is where a dispute lands and
 * where a number can be checked against paper.
 *
 * So each row that has a level beneath it opens it, and the trail of where you are is kept.
 * The server does the same grouped query at the new depth; nothing is assembled here.
 *
 * ## Making the level unmistakable
 *
 * A county called Nairobi, a constituency inside it, and a station inside that can all read
 * as "a place" in a plain table, and confusing them means comparing a station's 412 votes to
 * a county's 412,000 without noticing. Three things prevent that, and all three are
 * deliberate rather than decorative:
 *
 * 1. **The caption names the level of the rows** — "Showing polling stations in Kangemi" — so
 *    what you are looking at is stated, not inferred from the names.
 * 2. **Every row carries its type chip.** A row can never be mistaken for a different level
 *    even when the table is skimmed or screenshotted out of context.
 * 3. **The breadcrumb shows the full path in NAMES, never codes.** `KE.047.047_WES` is not a
 *    place anybody recognises; "Kenya › Nairobi › Westlands" is.
 */
export function PlaceBreakdown({
  electionId, seatType, statuses,
}: {
  electionId: string
  seatType: string
  statuses: string[] | null
}) {
  /** Where we are. Empty = the top, whatever the caller's scope makes that. */
  const [trail, setTrail] = useState<Crumb[]>([])
  const [rows, setRows] = useState<GeographyBreakdown[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [drilling, setDrilling] = useState<string | null>(null)

  const within = trail.length > 0 ? trail[trail.length - 1]!.id : null

  const load = useCallback(async () => {
    if (!electionId) { setRows([]); return }
    try {
      setError(null)
      setRows(null)
      setRows(await seatGeography({
        electionId, seatType, statuses, withinUnitId: within,
      }))
    } catch (err) {
      setError(explain(err))
      setRows([])
    }
    // statuses is a fresh array each render; its identity would refetch forever. The seat and
    // the widening are what actually change, and both are covered by the caller's key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [electionId, seatType, within, statuses === null])

  useEffect(() => { void load() }, [load])

  // Changing seat resets the trail: a constituency drilled into for one race is not a
  // meaningful place to still be standing when the race changes under you.
  useEffect(() => { setTrail([]) }, [seatType])

  const openRow = async (row: GeographyBreakdown) => {
    if (row.unitId == null || isLeaf(row.unitTypeCode)) return
    setDrilling(row.unitId)
    try {
      setTrail((t) => [...t, {
        id: row.unitId!, name: row.unitName, typeCode: row.unitTypeCode,
      }])
    } finally {
      setDrilling(null)
    }
  }

  const jumpTo = (index: number) => setTrail((t) => t.slice(0, index + 1))

  // What the rows below ARE — one level under where we stand.
  const childType = childTypeOf(trail.length > 0 ? trail[trail.length - 1]!.typeCode : null,
    rows)
  const here = trail.length > 0 ? trail[trail.length - 1]! : null

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-erp-sm font-medium">By place</h3>
        {rows !== null && rows.length > 0 && (
          <span className="text-erp-xs text-muted-foreground">
            {fmtNumber(rows.length)} {(childType
              ? (rows.length === 1 ? UNIT_TYPE_LABEL[childType] : UNIT_TYPE_PLURAL[childType])
              : 'place')?.toLowerCase()}
          </span>
        )}
      </div>

      {/* ── where you are, in names ─────────────────────────────────────── */}
      <nav aria-label="Place trail"
        className="axis-table-scroll flex items-center gap-1 text-erp-xs">
        <button type="button" onClick={() => setTrail([])}
          className={cn('inline-flex items-center gap-1 h-7 px-2 rounded-md',
            trail.length === 0
              ? 'bg-chocolate-soft/40 text-chocolate font-medium'
              : 'text-muted-foreground hover:text-foreground')}>
          <Home className="h-3 w-3" /> Everywhere
        </button>
        {trail.map((c, i) => (
          <span key={c.id} className="inline-flex items-center gap-1">
            <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
            <button type="button" onClick={() => jumpTo(i)}
              className={cn('h-7 px-2 rounded-md whitespace-nowrap',
                i === trail.length - 1
                  ? 'bg-chocolate-soft/40 text-chocolate font-medium'
                  : 'text-muted-foreground hover:text-foreground')}>
              {c.name}
              <span className="ml-1 opacity-70">
                {(UNIT_TYPE_LABEL[c.typeCode ?? ''] ?? '').toLowerCase()}
              </span>
            </button>
          </span>
        ))}
      </nav>

      {/* ── what the rows below are ─────────────────────────────────────── */}
      <p className="text-erp-xs text-muted-foreground">
        {childType ? (
          <>
            Showing <strong className="text-foreground">
              {UNIT_TYPE_PLURAL[childType]?.toLowerCase() ?? 'places'}
            </strong>
            {here ? <> in <strong className="text-foreground">{here.name}</strong></> : ' across everywhere you can see'}
            {childType === 'POLLING_STATION'
              ? ' — the smallest unit votes are counted at, so these figures come off the forms themselves.'
              : ' — each one opens to the level beneath it.'}
          </>
        ) : (
          'One level down from where you are looking, grouped by the database rather than assembled here.'
        )}
      </p>

      <ErrorStrip message={error} className="rounded-md border" />

      <ErpTable head={(
        <>
          <Th>Place</Th>
          <Th className="text-right">Units</Th>
          <Th className="text-right">Valid</Th>
          <Th className="text-right">Rejected</Th>
          <Th className="text-right">Registered</Th>
          <Th className="text-right">Turnout</Th>
          <Th />
        </>
      )}>
        {rows === null ? <LoadingRows colSpan={7} />
          : rows.length === 0 ? (
            <tr>
              <Td className="text-muted-foreground">
                {here
                  ? `Nothing counted inside ${here.name} yet.`
                  : 'Nothing counted yet.'}
              </Td>
            </tr>
          ) : rows.map((p) => {
            const leaf = isLeaf(p.unitTypeCode)
            const openable = p.unitId != null && !leaf
            return (
              <tr key={p.unitPath}
                onClick={openable ? () => void openRow(p) : undefined}
                className={cn(openable && 'cursor-pointer hover:bg-muted/40')}>
                <Td>
                  <span className="inline-flex items-center gap-1.5">
                    {leaf && <MapPin className="h-3 w-3 text-muted-foreground shrink-0" />}
                    <span className="font-medium">{p.unitName}</span>
                    {/* The type, on every row. This is what stops a station's figures being
                        read as a constituency's when the table is skimmed. */}
                    {p.unitTypeCode && (
                      <span className="text-erp-xs text-muted-foreground rounded border
                                       border-border px-1 py-0.5">
                        {UNIT_TYPE_LABEL[p.unitTypeCode] ?? p.unitTypeCode}
                      </span>
                    )}
                  </span>
                  {/* The full path in names, so a station is never just "Station 3". */}
                  {trail.length > 0 && (
                    <span className="block text-erp-xs text-muted-foreground truncate">
                      {trail.map((c) => c.name).join(' › ')} › {p.unitName}
                    </span>
                  )}
                  {p.unitId == null && (
                    <span className="ml-1.5 text-erp-xs text-amber-700 dark:text-amber-400">
                      unresolved place
                    </span>
                  )}
                </Td>
                <Td className="text-right tabular-nums">{fmtNumber(p.unitsReporting)}</Td>
                <Td className="text-right tabular-nums font-medium">{fmtNumber(p.validVotes)}</Td>
                <Td className="text-right tabular-nums">{fmtNumber(p.rejectedVotes)}</Td>
                <Td className="text-right tabular-nums">{fmtNumber(p.registeredVoters)}</Td>
                <Td className="text-right tabular-nums">
                  {p.turnoutPct == null ? '—' : `${p.turnoutPct}%`}
                </Td>
                <Td className="text-right">
                  {drilling === p.unitId ? <Loader2 className="h-3 w-3 animate-spin" />
                    : openable ? (
                      <span className="inline-flex items-center gap-0.5 text-erp-xs text-chocolate">
                        Open <ChevronRight className="h-3 w-3" />
                      </span>
                    ) : leaf ? (
                      <span className="text-erp-xs text-muted-foreground">station</span>
                    ) : null}
                </Td>
              </tr>
            )
          })}
      </ErpTable>

      {childType === 'POLLING_STATION' && rows !== null && rows.length > 0 && (
        <Note tone="info">
          These are the figures as filed on each station's form. Everything above this level —
          ward, constituency, county, national — is a sum of rows like these, which is why a
          disagreement is always settled at the station.
        </Note>
      )}
    </div>
  )
}

// ── bits ─────────────────────────────────────────────────────────────────────────────────

interface Crumb {
  id: string
  name: string
  typeCode: string | null
}

/** The polling station is where counting stops; nothing sits beneath it to open. */
const isLeaf = (typeCode: string | null) => typeCode === 'POLLING_STATION'

/**
 * What the listed rows are — the MOST COMMON type among them, not the first.
 *
 * <p>Taken from the rows rather than a hard-coded ladder because the server decides the
 * depth, and the caller's scope decides where "the top" is: a county-scoped user's first
 * level down is constituencies, and a ladder rooted at COUNTRY would caption that wrong.</p>
 *
 * <p>The mode rather than the first row because the buckets are NOT guaranteed uniform. The
 * grouping is {@code subpath(path, 0, least(depth, nlevel(path)))}, so a return filed ABOVE
 * the requested depth — a presidential return filed at the country rather than at a station —
 * buckets to itself and lands in the list as a shallower type. One such row must not relabel
 * the whole table; it carries its own chip, which is exactly how it should stand out.</p>
 */
function childTypeOf(parentType: string | null, rows: GeographyBreakdown[] | null): string | null {
  if (!rows || rows.length === 0) return null
  const counts = new Map<string, number>()
  for (const r of rows) {
    if (r.unitTypeCode) counts.set(r.unitTypeCode, (counts.get(r.unitTypeCode) ?? 0) + 1)
  }
  let best: string | null = null
  let bestN = 0
  for (const [type, n] of counts) {
    if (n > bestN) { best = type; bestN = n }
  }
  if (best) return best
  // Nothing resolved to a unit at all; fall back to the ladder below where we stand.
  const ladder = ['COUNTRY', 'COUNTY', 'CONSTITUENCY', 'WARD', 'POLLING_STATION']
  const i = ladder.indexOf(parentType ?? 'COUNTRY')
  return i >= 0 && i < ladder.length - 1 ? ladder[i + 1]! : null
}
